/**
 * @fileoverview Link management validation schemas
 *
 * Schemas for URL shortening operations including create, update, list, and delete.
 * These schemas validate user input for the Shlink API integration.
 *
 * @module shared/schemas/links
 */

import { z } from 'zod';

// =============================================================================
// Constants
// =============================================================================

/** Maximum allowed title length */
export const MAX_TITLE_LENGTH = 255;

/** Maximum allowed tag length */
export const MAX_TAG_LENGTH = 50;

/** Maximum number of tags per link */
export const MAX_TAGS_PER_LINK = 10;

/** Maximum items per page for pagination */
export const MAX_ITEMS_PER_PAGE = 100;

/** Default items per page */
export const DEFAULT_ITEMS_PER_PAGE = 20;

// =============================================================================
// Base Schemas (Reusable Components)
// =============================================================================

/**
 * Normalize URL by adding https:// if no protocol is provided
 * Handles: www.example.com, example.com, http://example.com, https://example.com
 */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();

  // If it already has a protocol, return as-is
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return trimmed;
  }

  // Add https:// for URLs without protocol
  return `https://${trimmed}`;
}

/**
 * Valid URL schema with strict validation.
 * Automatically adds https:// if no protocol provided (e.g., www.google.com → https://www.google.com)
 * Requires a proper domain with at least one dot (e.g., example.com, not just "test")
 * Rejects javascript:, data:, and other dangerous protocols.
 */
export const urlSchema = z
  .string()
  .min(1, 'URL is required')
  .transform(normalizeUrl)
  .refine(
    (url) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Please enter a valid URL' }
  )
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        // Only allow http and https protocols
        return ['http:', 'https:'].includes(parsed.protocol);
      } catch {
        return false;
      }
    },
    { message: 'URL must use http or https protocol' }
  )
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        // Require a proper domain with at least one dot (e.g., example.com)
        // This prevents single-word entries like "test" from being accepted
        // Exception: localhost for development
        const hostname = parsed.hostname.toLowerCase();
        return hostname === 'localhost' || hostname.includes('.');
      } catch {
        return false;
      }
    },
    { message: 'Please enter a complete URL with a domain (e.g., example.com)' }
  );

/**
 * Title schema with length constraint.
 */
export const titleSchema = z
  .string()
  .max(MAX_TITLE_LENGTH, `Title must be ${MAX_TITLE_LENGTH} characters or less`)
  .optional();

/**
 * Single tag schema.
 */
export const tagSchema = z
  .string()
  .min(1, 'Tag cannot be empty')
  .max(MAX_TAG_LENGTH, `Tag must be ${MAX_TAG_LENGTH} characters or less`)
  .regex(/^[a-zA-Z0-9-_]+$/, 'Tags can only contain letters, numbers, hyphens, and underscores');

/**
 * Tags array schema with limits.
 */
export const tagsSchema = z
  .array(tagSchema)
  .max(MAX_TAGS_PER_LINK, `Maximum ${MAX_TAGS_PER_LINK} tags allowed`)
  .optional();

/**
 * Datetime schema for expiration dates.
 * Accepts ISO 8601 datetime strings.
 */
export const validUntilSchema = z
  .string()
  .datetime({ message: 'Invalid datetime format' })
  .optional()
  .nullable();

/**
 * Max visits schema for link expiration by visit count.
 */
export const maxVisitsSchema = z
  .number()
  .int('Must be a whole number')
  .positive('Must be a positive number')
  .max(1_000_000, 'Maximum 1,000,000 visits')
  .optional()
  .nullable();

// =============================================================================
// Link Operation Schemas
// =============================================================================

/**
 * Custom short code (vanity URL) schema.
 * Plus-only feature. 3-20 alphanumeric characters, hyphens, and underscores.
 * Empty strings are treated as "no custom code" (allowing auto-generation).
 * Note: Empty string validation is handled at form submission, not schema level,
 * to maintain type compatibility with react-hook-form's zodResolver.
 */
export const customCodeSchema = z
  .string()
  .max(20, 'Custom code must be 20 characters or less')
  .refine((val) => val === '' || val.length >= 3, {
    message: 'Custom code must be at least 3 characters',
  })
  .refine((val) => val === '' || /^[a-zA-Z0-9-_]+$/.test(val), {
    message: 'Custom code can only contain letters, numbers, hyphens, and underscores',
  })
  .optional();

/**
 * Schema for creating a new short URL.
 *
 * Required fields:
 * - url: The destination URL to shorten
 *
 * Optional fields:
 * - title: Human-readable title for the link
 * - tags: Array of tags for organization
 * - validUntil: Expiration datetime
 * - maxVisits: Maximum number of visits before expiration
 * - customCode: Custom short code (Plus only)
 * - projectId: Project to organize link into (Plus only)
 *
 * @example
 * ```typescript
 * const input = createLinkSchema.parse({
 *   url: 'https://example.com/long-path',
 *   title: 'Example Link',
 *   tags: ['marketing', 'campaign-2024'],
 *   customCode: 'my-brand', // Plus only
 * });
 * ```
 */
export const createLinkSchema = z.object({
  url: urlSchema,
  title: titleSchema,
  tags: tagsSchema,
  validUntil: validUntilSchema,
  maxVisits: maxVisitsSchema,
  customCode: customCodeSchema,
  projectId: z.string().optional(),
});

/** TypeScript type for create link input */
export type CreateLinkInput = z.infer<typeof createLinkSchema>;

/**
 * Schema for updating an existing short URL.
 * All fields are optional since partial updates are allowed.
 */
export const updateLinkSchema = z.object({
  title: titleSchema,
  tags: tagsSchema,
  validUntil: validUntilSchema,
  maxVisits: maxVisitsSchema,
  /** New destination URL (triggers Safe Browsing validation on backend) */
  destinationUrl: urlSchema.optional(),
});

/** TypeScript type for update link input */
export type UpdateLinkInput = z.infer<typeof updateLinkSchema>;

/**
 * Schema for listing short URLs with pagination and filtering.
 *
 * Query parameters:
 * - page: Page number (1-indexed)
 * - itemsPerPage: Items per page (max 100)
 * - searchTerm: Text search across URL and title
 * - tags: Comma-separated list of tags to filter by
 * - orderBy: Field to sort by
 * - orderDir: Sort direction (ASC/DESC)
 */
export const listLinksQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  itemsPerPage: z.coerce
    .number()
    .int()
    .positive()
    .max(MAX_ITEMS_PER_PAGE)
    .default(DEFAULT_ITEMS_PER_PAGE),
  searchTerm: z.string().max(200).optional(),
  tags: z.string().max(500).optional(), // comma-separated
  orderBy: z.enum(['dateCreated', 'shortCode', 'visits']).default('dateCreated'),
  orderDir: z.enum(['ASC', 'DESC']).default('DESC'),
});

/** TypeScript type for list links query */
export type ListLinksQuery = z.infer<typeof listLinksQuerySchema>;

/**
 * Schema for the external API shorten endpoint.
 * Simpler than the full create schema for API ergonomics.
 */
export const shortenApiSchema = z.object({
  url: urlSchema,
  title: titleSchema,
});

/** TypeScript type for shorten API input */
export type ShortenApiInput = z.infer<typeof shortenApiSchema>;
